import { Controller } from "@hotwired/stimulus"

// A step may render several stepForm/associationForm pairs (one per subscriber type on the
// amendment subscriber step, each in its own pane): every pair is wired, and a submit is resolved
// against the association form of ITS pane, never the first one found in the document.
export default class extends Controller {
  static targets = ['stepForm', 'associationForm']

  connect() {
    // On lie les handlers une seule fois : add/removeEventListener doivent partager la même
    // référence, sinon les écouteurs s'accumulent à chaque étape (le disconnect ne retirait
    // qu'une référence jamais enregistrée) et des handlers "submit" périmés finissaient par
    // intercepter et preventDefault() la soumission → bouton "Suivant" inopérant.
    this.boundStepFormSubmitListener = () => this.stepFormSubmitListener();
    this.boundToggleSubmitButtonsVisibility = () => this.toggleSubmitButtonsVisibility();
    this.boundHandleStepFormSubmit = this.handleStepFormSubmit.bind(this);

    this.stepFormSubmitListener();
    document.addEventListener("turbo:frame-load", this.boundStepFormSubmitListener);
    document.addEventListener("turbo:frame-load", this.boundToggleSubmitButtonsVisibility);
  }

  stepFormSubmitListener() {
    this.stepFormTargets.forEach((stepForm) => {
      stepForm.removeEventListener("submit", this.boundHandleStepFormSubmit);
      stepForm.addEventListener("submit", this.boundHandleStepFormSubmit);
    });
  }

  handleStepFormSubmit(event) {
    const stepForm = event.currentTarget;
    const associationForm = this.associationFormFor(stepForm);
    if (!associationForm) return;

    const stepFormSubmitButton = stepForm.querySelector("button[type='submit'], input[type='submit']");

    const stepIsSkippable = stepForm.querySelector("input[name='step_is_skippable']")?.value === 'true';

    // form.elements also covers a control bound by its `form` attribute (the admin id line sits above the selector).
    const hiddenFieldInput = Array.from(stepForm.elements).find((el) => el.dataset && el.dataset.relationSelectorTarget === 'hiddenField') || null;
    // Only the association form's own controls: an index embedded in it (users search of the
    // representative picker) brings its own <form>, whose inputs and submit button must be ignored.
    const ownControls = (selector) => Array.from(associationForm.querySelectorAll(selector)).filter((el) => el.form === associationForm);
    const isAnyInputFilled = ownControls("input:not([type='hidden']):not([type='submit'])").some(input => input.value.trim() !== "");

    const stepHasRelationSelector = hiddenFieldInput !== null;
    const isHiddenFieldFilled = hiddenFieldInput && hiddenFieldInput.value.trim() !== "";
    const associationFormIsAssociationSelector = associationForm.getAttribute('data-association-selector') === 'true'

    const shouldIntercept =
      (isAnyInputFilled && !isHiddenFieldFilled) ||
      associationFormIsAssociationSelector ||
      (stepHasRelationSelector && !stepIsSkippable && !isHiddenFieldFilled);

    if (shouldIntercept) {
      event.preventDefault(); // Empêche la soumission du formulaire d'étape
      const associationFormSubmitButton = ownControls("button[type='submit'], input[type='submit']")[0];
      associationFormSubmitButton?.click(); // Soumet le formulaire d'association de manière programmatique
      stepFormSubmitButton?.removeAttribute('disabled');
    }

    // Si la step ne peut pas être skippée, on renvoie vers le form d'association dans tous les cas
    // Si isHiddenFieldFilled est vrai, le formulaire d'étape sera soumis même si isAnyInputFilled est vrai
    // Pas besoin d'ajouter de code supplémentaire pour gérer cela, car le comportement par défaut de soumission du formulaire s'appliquera
  }

  // The association form sharing the smallest container with the step form. Walks up from the step
  // form so a pane's own association form wins over a sibling pane's; a single-pair step resolves
  // exactly as before.
  associationFormFor(stepForm) {
    let scope = stepForm.parentElement;
    while (scope && scope !== this.element.parentElement) {
      const match = this.associationFormTargets.find((form) => scope.contains(form));
      if (match) return match;
      scope = scope.parentElement;
    }
    return null;
  }

  toggleSubmitButtonsVisibility() {
    this.stepFormTargets.forEach((stepForm) => {
      const submitButton = stepForm.querySelector("input[type='submit']");
      if (!submitButton) return;

      // Sometimes the target doesnt work, especially in this context. So we exceptionnally pass the class
      // instead — only when the step has a single form, where the document-wide lookup is unambiguous.
      const fallback = this.stepFormTargets.length === 1 ? document.querySelector('.association-form') : null;
      const associationForm = this.associationFormFor(stepForm) || fallback;
      const turboComponentNewRecord = associationForm?.querySelector("input[name='turbo_component_new_record']")?.value === 'true';
      submitButton.classList.toggle('d-none', Boolean(associationForm && turboComponentNewRecord));
    });
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.boundStepFormSubmitListener);
    document.removeEventListener("turbo:frame-load", this.boundToggleSubmitButtonsVisibility);
    this.stepFormTargets.forEach((stepForm) => {
      stepForm.removeEventListener("submit", this.boundHandleStepFormSubmit);
    });
  }
}
